'use client';

import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface CardProps {
  children: React.ReactNode;
  variant?: 'default' | 'hover' | 'tilt';
  className?: string;
  onClick?: () => void;
}

export function Card({ children, variant = 'default', className, onClick }: CardProps) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (variant !== 'tilt' || !cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;

    setTilt({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    if (variant === 'tilt') {
      setTilt({ x: 0, y: 0 });
    }
  };

  const baseClasses = 'bg-white border border-border rounded-sm shadow-sm';

  const hoverProps = {
    whileHover: { y: -4 },
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  };

  return (
    <motion.div
      ref={cardRef}
      className={cn(
        baseClasses,
        variant === 'hover' && 'hover:shadow-[0_12px_40px_rgba(15,23,42,0.14)]',
        variant === 'tilt' && '[@transform-style:preserve-3d] transition-transform duration-100 ease-out',
        className
      )}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      {...(variant === 'hover' ? hoverProps : {})}
      animate={variant === 'tilt' ? { rotateX: tilt.x, rotateY: tilt.y } : undefined}
    >
      {children}
    </motion.div>
  );
}
